import type { Dispatch, SetStateAction } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import ExtraDetailRow from "./ExtraDetailRow";
import type { DBInvoice } from "@/lib/db-types";
import type { useProjectExtras } from "@/hooks/use-project-extras";
import type { BudgetLineRow } from "@/hooks/use-project-detail";
import type { ExtraForm } from "./StatisterTab";
import type { ExtraContract } from "@/hooks/use-extra-contracts";
import { SrIcon } from "@/components/ui/SrIcon";

interface StatisterTableProps {
  projectId: string;
  extras: ReturnType<typeof useProjectExtras>["data"] extends (infer T)[] ? T[] : [];
  isLoading: boolean;
  userId: string | null;
  invoices: DBInvoice[];
  budgetLines: BudgetLineRow[];
  adding: boolean;
  onSetAdding: (v: boolean) => void;
  form: ExtraForm;
  onSetForm: Dispatch<SetStateAction<ExtraForm>>;
  onAdd: () => void;
  createMutIsPending: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  selectableCount: number;
  /** VIB-1281: kontrakt-kolonnen er bak feature_contracts (besluttet i StatisterTab). */
  showContracts: boolean;
  contractMap: Map<string, ExtraContract>;
}

export default function StatisterTable({
  projectId,
  extras,
  isLoading,
  userId,
  invoices,
  budgetLines,
  adding,
  onSetAdding,
  form,
  onSetForm,
  onAdd,
  createMutIsPending,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  selectableCount,
  showContracts,
  contractMap,
}: StatisterTableProps) {
  const count = extras.length;
  const allSelected = selectableCount > 0 && selectedIds.size === selectableCount;
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <div className="border-t">
      {isLoading ? (
        <div className="flex justify-center py-4">
          <SrIcon name="loader2" className="w-4 h-4 animate-spin"  />
        </div>
      ) : count === 0 && !adding ? (
        <div className="flex flex-col items-center justify-center py-12 text-center px-4">
          <p className="text-sm text-muted-foreground">
            Ingen statister eller lønnede eksterne registrert for dette prosjektet.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Gjelder statister, frilansere, og annet lønnet arbeid utført av eksterne.
          </p>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="w-10 py-2 px-2">
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={onToggleSelectAll}
                  disabled={selectableCount === 0}
                  aria-label="Velg alle"
                />
              </th>
              <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                Navn
              </th>
              <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider w-32">
                Rolle
              </th>
              <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider w-36">
                Tlf
              </th>
              <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                E-post
              </th>
              <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider w-48">
                Budsjettlinje
              </th>
              <th className="text-right py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider w-28">
                Avtalt pris
              </th>
              <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider w-44">
                Status
              </th>
              {showContracts && (
                <th className="text-left py-2 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider w-32">
                  Kontrakt
                </th>
              )}
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {extras.map((ex) => (
              <ExtraDetailRow
                key={ex.id}
                ex={ex}
                projectId={projectId}
                userId={userId}
                invoices={invoices}
                budgetLines={budgetLines}
                selected={selectedIds.has(ex.id)}
                onToggleSelect={() => onToggleSelect(ex.id)}
                showContracts={showContracts}
                contract={contractMap.get(ex.id)}
              />
            ))}
            {adding && (
              <tr className="border-b border-border/50 bg-muted/20">
                <td colSpan={showContracts ? 10 : 9} className="py-3 px-4">
                  <div className="space-y-2">
                    {/* Rad 1: kontaktinfo */}
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                          Navn
                        </label>
                        <input
                          className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm"
                          placeholder="Fullt navn..."
                          value={form.full_name}
                          onChange={(e) => onSetForm((f) => ({ ...f, full_name: e.target.value }))}
                          autoFocus
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                          Rolle
                        </label>
                        <input
                          className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm"
                          placeholder="F.eks. Statist"
                          value={form.role}
                          onChange={(e) => onSetForm((f) => ({ ...f, role: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                          Telefon
                        </label>
                        <input
                          className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm"
                          placeholder="Telefon"
                          value={form.phone_number}
                          onChange={(e) => onSetForm((f) => ({ ...f, phone_number: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                          E-post
                        </label>
                        <input
                          className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm"
                          placeholder="E-post"
                          type="email"
                          value={form.email}
                          onChange={(e) => onSetForm((f) => ({ ...f, email: e.target.value }))}
                        />
                      </div>
                    </div>
                    {/* Rad 2: bank/budsjett/pris */}
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                          Kontonummer
                        </label>
                        <input
                          className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm"
                          placeholder="Kontonummer"
                          value={form.bank_account}
                          onChange={(e) => onSetForm((f) => ({ ...f, bank_account: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                          Budsjettlinje
                        </label>
                        <select
                          className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm"
                          value={form.budget_line_id}
                          onChange={(e) => onSetForm((f) => ({ ...f, budget_line_id: e.target.value }))}
                        >
                          <option value="">Ingen budsjettlinje</option>
                          {budgetLines.map((bl) => (
                            <option key={bl.id} value={bl.id}>
                              {bl.description ?? "Uten navn"}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                          Avtalt pris
                        </label>
                        <input
                          type="number"
                          className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm text-right"
                          placeholder="Pris"
                          value={form.agreed_amount}
                          onChange={(e) => onSetForm((f) => ({ ...f, agreed_amount: e.target.value }))}
                        />
                      </div>
                    </div>
                    {/* Knapperad */}
                    <div className="flex gap-1 justify-end pt-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => onSetAdding(false)}
                      >
                        <SrIcon name="x" className="w-3 h-3 mr-1" /> Avbryt
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 text-xs"
                        onClick={onAdd}
                        disabled={createMutIsPending || !form.full_name}
                      >
                        {createMutIsPending ? (
                          <SrIcon name="loader2" className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <SrIcon name="check" className="w-3 h-3 mr-1" />
                        )}
                        Lagre
                      </Button>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
